import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { CheckCircle, LocalShipping } from '@mui/icons-material';
import { transportPortalService } from '../../services/transportPortalService';
import MilestoneUploadCard from '../../components/transport/MilestoneUploadCard';
import { BrandLogo } from '../../branding/BrandLogo';
import { formatCurrency } from '../../components/CurrencyTextField';
import { formatDateOnly } from '../../utils/dateUtils';
import {
  TRANSPORT_STATUS_ORDER,
  formatDateInput,
  formatTimeInput,
  transportJobTypeLabel,
  transportStatusMeta,
} from '../../constants/transportation';
import { FW } from '../../theme/tokens';

function formatWhen(dateVal, timeVal) {
  const d = formatDateOnly(formatDateInput(dateVal));
  const t = formatTimeInput(timeVal);
  if (!d && !t) return '—';
  if (d && t) {
    const [hh, mm] = t.split(':');
    const h = Number(hh);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${d} · ${h12}:${mm} ${suffix}`;
  }
  return d || t;
}

const TransportationPortal = () => {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingKind, setUploadingKind] = useState('');
  const [acting, setActing] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await transportPortalService.getJob(token);
      setJob(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Job not found');
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const closed = job && ['complete', 'cancelled'].includes(job.status);
  const waiting = job?.status === 'pending_approval';
  const filesByKind = (kind) => (job?.files || []).filter((f) => f.kind === kind);

  const upload = async (kind, file) => {
    setUploadingKind(kind);
    try {
      await transportPortalService.uploadFile(token, { file, kind });
      const res = await transportPortalService.getJob(token);
      setJob(res.data);
    } finally {
      setUploadingKind('');
    }
  };

  const run = async (key, fn) => {
    setActing(key);
    setError('');
    try {
      const res = await fn(token);
      setJob(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setActing('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: FW.surface }}>
      <Box
        sx={{
          bgcolor: FW.navy,
          color: '#fff',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        <BrandLogo
          slot="header"
          sx={{
            height: 36,
            width: 36,
            flexShrink: 0,
            overflow: 'hidden',
            borderRadius: 1,
          }}
          imgSx={{
            height: 36,
            width: 36,
            maxHeight: 36,
            maxWidth: 36,
            objectFit: 'contain',
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1 }}>
            Transportation Portal
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.2 }}>
            Driver uploads
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 560, mx: 'auto', p: 2, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !job ? (
          <Alert severity="error">{error || 'This driver link is invalid or expired.'}</Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocalShipping color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                  {job.identifier}
                </Typography>
                <Chip size="small" label={transportStatusMeta(job.status).label} color={transportStatusMeta(job.status).color} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {transportJobTypeLabel(job.job_type)}
                {job.work_order_identifier ? ` · ${job.work_order_identifier}` : ''}
                {job.customer_name ? ` · ${job.customer_name}` : ''}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pickup</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{job.pickup_name || '—'}</Typography>
                  <Typography variant="body2">{job.pickup_address || '—'}</Typography>
                  <Typography variant="body2" color="text.secondary">{formatWhen(job.pickup_date, job.pickup_time)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Drop off</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{job.dropoff_name || '—'}</Typography>
                  <Typography variant="body2">{job.dropoff_address || '—'}</Typography>
                  <Typography variant="body2" color="text.secondary">{formatWhen(job.dropoff_date, job.dropoff_time)}</Typography>
                </Box>
                {job.total != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Pay amount</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(job.total)}</Typography>
                    {(Number(job.rate) > 0 || Number(job.fuel_fees) > 0) && (
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(job.rate)} rate
                        {Number(job.fuel_fees) > 0 ? ` + ${formatCurrency(job.fuel_fees)} fuel/fees` : ''}
                      </Typography>
                    )}
                  </Box>
                )}
                {job.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Instructions</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{job.notes}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Progress</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                {TRANSPORT_STATUS_ORDER.map((key) => {
                  const reached = TRANSPORT_STATUS_ORDER.indexOf(job.status) >= TRANSPORT_STATUS_ORDER.indexOf(key)
                    && job.status !== 'cancelled';
                  return (
                    <Box key={key} sx={{ flex: 1, textAlign: 'center' }}>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 99,
                          bgcolor: reached ? FW.steel : FW.line,
                          mb: 0.5,
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: reached ? FW.navy : FW.muted }}>
                        {transportStatusMeta(key).label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {waiting && (
              <Alert severity="info" icon={<CheckCircle />} sx={{ mb: 2 }}>
                Submitted. The office has been notified and will review this job.
              </Alert>
            )}
            {job.status === 'complete' && (
              <Alert severity="success" sx={{ mb: 2 }}>This job is complete. Thank you.</Alert>
            )}
            {job.status === 'cancelled' && (
              <Alert severity="warning" sx={{ mb: 2 }}>This job was cancelled.</Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <MilestoneUploadCard
                title="1. Proof of Pickup"
                hint="Take a photo or choose a file from your phone"
                emptyLabel="No photo uploaded yet"
                capture
                files={filesByKind('pickup')}
                disabled={closed || waiting}
                uploading={uploadingKind === 'pickup'}
                onUpload={(file) => upload('pickup', file)}
                downloadHref={(file) => transportPortalService.downloadUrl(token, file.id)}
              />
              <MilestoneUploadCard
                title="2. Proof of Delivery"
                hint="Take a photo or choose a file from your phone"
                emptyLabel="No photo uploaded yet"
                capture
                files={filesByKind('delivery')}
                disabled={closed || waiting}
                uploading={uploadingKind === 'delivery'}
                onUpload={(file) => upload('delivery', file)}
                downloadHref={(file) => transportPortalService.downloadUrl(token, file.id)}
              />
              <MilestoneUploadCard
                title="3. Signed documents / receipts"
                hint="Upload packing slip, POD, or other signed docs"
                emptyLabel="No documents uploaded"
                accept="image/*,.pdf,application/pdf"
                icon="document"
                files={filesByKind('document')}
                disabled={closed || waiting}
                uploading={uploadingKind === 'document'}
                onUpload={(file) => upload('document', file)}
                downloadHref={(file) => transportPortalService.downloadUrl(token, file.id)}
              />
            </Box>

            {!closed && !waiting && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {job.status === 'assigned' && (
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={!!acting}
                    onClick={() => run('pickup', transportPortalService.markPickedUp)}
                    sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    Mark Picked Up
                  </Button>
                )}
                {['assigned', 'picked_up'].includes(job.status) && (
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={!!acting}
                    onClick={() => run('delivered', transportPortalService.markDelivered)}
                    sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    Mark Delivered
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  disabled={!!acting}
                  onClick={() => run('complete', transportPortalService.complete)}
                  sx={{ minHeight: 52, textTransform: 'none', fontWeight: 700 }}
                >
                  Mark Complete — send for approval
                </Button>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Requires pickup and delivery photos. The office is emailed when you mark complete.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TransportationPortal;
